import * as actionType from "./actionTypes";
import { getRequestAuthed, postRequest, postRequestAuthed, processError, setGetState, setAuthTokenDispatch } from "../../utils/apiHandlers";
import { LOADING_STATE, LS_ITEM, NOTIF_TYPE, AUTH_API } from "../../utils/stateTypes";
import _notif from "../notification";
import { storeTokens, clearTokens, getToken, getRefreshToken } from "../localStorage";

/* =========================== AUTH =========================== */

export const setAuthToken = (token) => (
  {
    type: actionType.SET_AUTH_TOKEN,
    token,
  }
);

export const setHasCheckedAuth = () => (
  {
    type: actionType.SET_HAS_CHECKED_AUTH,
  }
);

const setProfile = (profile) => (
  {
    type: actionType.SET_PROFILE,
    profile,
  }
);

//Loads the logged in member's profile (also confirms the token is valid, refreshing it if needed). The admin app uses
//it too, with the admin's login
const loadProfile = (successCB=null, errorCB=null, tokenOverride=null) => (
  (dispatch) => {
    getRequestAuthed("/Member/Me",
      async (profile) => {
        //Take the latest token from storage in case it was refreshed along the way
        const latestAuthToken = await getToken();
        dispatch(setAuthToken(latestAuthToken));
        dispatch(setProfile(profile));
        if (successCB) {
          successCB(profile);
        }
      },
      () => {
        clearTokens();
        dispatch(setAuthToken(null));
        if (errorCB) {
          errorCB();
        }
      },
      () => {
        //Timeout - keep the tokens (the member may just be offline) but carry on logged out
        dispatch(_notif.actions.queueNotification("Unable to connect", NOTIF_TYPE.ERROR));
        if (errorCB) {
          errorCB();
        }
      },
      tokenOverride,
    );
  }
);

export const initAuth = () => (
  (dispatch, getState) => {
    // Send store methods to api class so that auth token can be managed
    setGetState(getState);
    setAuthTokenDispatch((authToken) => dispatch(setAuthToken(authToken)));

    const checkAuthed = async () => {
      const authToken = await getToken();
      if (!authToken) {
        //No auth token saved - logged out
        dispatch(setHasCheckedAuth());
        return;
      }
      dispatch(loadProfile(
        () => dispatch(setHasCheckedAuth()),
        () => dispatch(setHasCheckedAuth()),
        authToken,
      ));
    };
    checkAuthed();
  }
);

//Stores the tokens from a login / sign up / password reset, then loads the profile
const completeLogin = (dispatch, lsItem, tokens, successCB) => {
  storeTokens(tokens.token, tokens.refreshToken);
  dispatch(setAuthToken(tokens.token));
  dispatch(loadProfile(
    (profile) => {
      dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
      if (successCB) {
        successCB(profile);
      }
    },
    () => dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.ERROR)),
    tokens.token,
  ));
};

/* =========================== LOGIN & SIGN UP =========================== */

export const login = (email, password, isAdmin=false, successCB=null) => (
  (dispatch) => {
    const lsItem = LS_ITEM.LOGIN;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    postRequest(isAdmin ? "/Account/AdminLogin" : "/Member/Login",
      {
        email,
        password,
      },
      (tokens) => completeLogin(dispatch, lsItem, tokens, successCB),
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const register = (details, successCB=null) => (
  (dispatch) => {
    const lsItem = LS_ITEM.REGISTER;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    postRequest("/Member/Register",
      details,
      (tokens) => completeLogin(dispatch, lsItem, tokens, successCB),
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const forgotPassword = (email, successCB) => (
  (dispatch) => {
    const lsItem = LS_ITEM.FORGOT;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    postRequest("/Member/ForgotPassword",
      { email },
      () => {
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
        successCB();
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const resetPassword = (email, token, newPassword, successCB) => (
  (dispatch) => {
    const lsItem = LS_ITEM.RESET;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    postRequest("/Member/ResetPassword",
      { email, token, newPassword },
      (tokens) => completeLogin(dispatch, lsItem, tokens, successCB),
      processError(dispatch, _notif, lsItem),
    );
  }
);

/* =========================== PROFILE =========================== */

export const updateProfile = (details) => (
  (dispatch, getState) => {
    const lsItem = LS_ITEM.PROFILE;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    postRequestAuthed("/Member/UpdateProfile",
      details,
      () => {
        dispatch(setProfile({ ...getState().auth.profile, ...details }));
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
        dispatch(_notif.actions.queueNotification("Details saved", NOTIF_TYPE.SUCCESS));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

export const changePassword = (currentPassword, newPassword, successCB, lsItem=LS_ITEM.PASSWORD) => (
  (dispatch) => {
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));

    postRequestAuthed(`${AUTH_API}/ChangePassword`,
      { currentPassword, newPassword },
      () => {
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
        dispatch(_notif.actions.queueNotification("Password changed", NOTIF_TYPE.SUCCESS));
        successCB();
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

/* =========================== LOGOUT =========================== */

export const logout = () => (
  async (dispatch) => {
    //Ends this login on the server (its refresh token), even if the access token has expired
    const refreshToken = await getRefreshToken();
    postRequest(`${AUTH_API}/Logout`,
      { refreshToken },
      () => {/* Do nothing */},
      () => {/* Do nothing */},
    );
    dispatch(setAuthToken(null));
    clearTokens();
    dispatch({
      type: actionType.LOGOUT,
    });
    setTimeout(() => {
      dispatch(setHasCheckedAuth());
    }, 100);
  }
);
